/** API Administration — who calls the API, how much, and what breaks.
 *
 * The platform standard asks for this without anyone opening Application Insights: calling
 * applications and consumers, key status, request volume, response times, error rates,
 * rate-limit activity and recent failures. Counted for machine callers only (API keys,
 * Claude sessions, applications); the web app is not an integration.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState, ErrorState, Loading, Pill, SectionHeader, StatCard } from '@/components/ui'
import { useApiUsage } from '@/features/api-access/useApiAccess'

export function ApiUsageTab() {
  const [days, setDays] = useState(30)
  const { data, isPending, error } = useApiUsage(days)
  if (isPending) return <Loading label="Counting API calls" />
  if (error) return <ErrorState error={error} />
  if (!data) return null

  const total = data.consumers.reduce((sum, c) => sum + c.requests, 0)
  const errors = data.consumers.reduce((sum, c) => sum + c.clientErrors + c.serverErrors, 0)
  const limited = data.consumers.reduce((sum, c) => sum + c.rateLimited, 0)

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-[70ch] text-sm text-fg-muted">
          Machine callers — API keys, Claude sessions and applications — over the last{' '}
          <select
            className="rounded border border-border bg-surface px-1 text-sm text-fg"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[1, 7, 30, 90].map((d) => (
              <option key={d} value={d}>
                {d} {d === 1 ? 'day' : 'days'}
              </option>
            ))}
          </select>
          . Keys are managed on the <Link to="/api-access" className="underline">API Access</Link>{' '}
          page; reports from assistants are in{' '}
          <Link to="/api-feedback" className="underline">
            API Feedback
          </Link>
          .
        </p>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        <StatCard label="Requests" value={total.toLocaleString()} />
        <StatCard label="Errors" value={total ? `${((errors / total) * 100).toFixed(1)}%` : '—'} />
        <StatCard label="Rate-limited" value={limited.toLocaleString()} />
        <StatCard label="Active keys" value={String(data.keys.active)} />
      </div>

      <div className="mb-6 flex flex-wrap gap-2 text-sm text-fg-muted">
        <span>
          Limit {data.rateLimitPerMinute} requests per minute per key ·{' '}
          {data.keys.expiringWithin14Days} key(s) expire within 14 days ·
        </span>
        {data.keys.allowedFromAnywhere > 0 ? (
          <Pill tone="amber">{data.keys.allowedFromAnywhere} usable from anywhere</Pill>
        ) : (
          <span>none usable from anywhere</span>
        )}
      </div>

      <SectionHeader>Consumers</SectionHeader>
      {data.consumers.length === 0 ? (
        <EmptyState title="No machine calls in this period">
          Calls made with an API key, a Claude session or an application appear here.
        </EmptyState>
      ) : (
        <div className="card mb-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-fg-muted">
                <th className="px-4 py-2 font-normal">Credential</th>
                <th className="px-4 py-2 font-normal">Owner</th>
                <th className="px-4 py-2 text-right font-normal">Requests</th>
                <th className="px-4 py-2 text-right font-normal">4xx</th>
                <th className="px-4 py-2 text-right font-normal">5xx</th>
                <th className="px-4 py-2 text-right font-normal">429</th>
                <th className="px-4 py-2 text-right font-normal">Avg ms</th>
                <th className="px-4 py-2 font-normal">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.consumers.map((c) => (
                <tr key={c.credential} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2">
                    <code className="text-xs">{c.credential}</code>
                  </td>
                  <td className="px-4 py-2 text-fg-muted">{c.owner}</td>
                  <td className="px-4 py-2 text-right">{c.requests.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{c.clientErrors}</td>
                  <td className="px-4 py-2 text-right">{c.serverErrors}</td>
                  <td className="px-4 py-2 text-right">{c.rateLimited}</td>
                  <td className="px-4 py-2 text-right">{c.averageMs}</td>
                  <td className="px-4 py-2 text-fg-muted">
                    {c.lastSeenAt ? new Date(c.lastSeenAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SectionHeader>Applications allowed in</SectionHeader>
      <ul className="card mb-6 divide-y divide-border text-sm">
        {data.applications.length === 0 ? (
          <li className="px-4 py-3 text-fg-muted">None listed on this environment.</li>
        ) : (
          data.applications.map((app) => (
            <li key={app.clientId} className="flex justify-between px-4 py-3">
              <span className="text-fg">{app.name}</span>
              <code className="text-xs text-fg-muted">{app.clientId}</code>
            </li>
          ))
        )}
      </ul>

      <SectionHeader>Recent failures</SectionHeader>
      {data.recentFailures.length === 0 ? (
        <EmptyState title="No failed machine calls">Nothing has gone wrong recently.</EmptyState>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-fg-muted">
                <th className="px-4 py-2 font-normal">When</th>
                <th className="px-4 py-2 font-normal">Credential</th>
                <th className="px-4 py-2 font-normal">Call</th>
                <th className="px-4 py-2 font-normal">Status</th>
                <th className="px-4 py-2 font-normal">Reference</th>
              </tr>
            </thead>
            <tbody>
              {data.recentFailures.map((f) => (
                <tr key={`${f.occurredAt}-${f.correlationId}`} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2 whitespace-nowrap text-fg-muted">
                    {new Date(f.occurredAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <code className="text-xs">{f.credential}</code>
                    <div className="text-xs text-fg-muted">{f.owner}</div>
                  </td>
                  <td className="px-4 py-2">
                    <code className="text-xs">
                      {f.method} {f.path}
                    </code>
                  </td>
                  <td className="px-4 py-2">
                    <Pill tone={f.statusCode >= 500 ? 'red' : f.statusCode === 429 ? 'amber' : 'neutral'}>
                      {f.statusCode}
                    </Pill>
                  </td>
                  <td className="px-4 py-2">
                    <code className="text-xs text-fg-muted">{f.correlationId}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
